import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Check, Skull, Crown, Loader2, BadgeDollarSign, MessageSquare, BarChart3 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";

const PLANS = [
    {
        id: "pro",
        name: "Pro",
        price: "$13",
        per: "/ week",
        accent: "#a855f7",
        aura: "aura-pro-purple",
        icon: Crown,
        cta: "Subscribe Pro",
        features: ["Daily Free Goblin Pick", "2 Pro Cards (Blue + Purple Aura)", "Bonus Holo Pick (6th card)", "Risk badges per card", "Cancel anytime"],
        ribbon: "Most popular",
        savingsLine: "Less than $2 a day for week-long pro access",
    },
    {
        id: "sports_premium",
        name: "Sports Premium",
        price: "$23",
        per: "/ week",
        accent: "#ef4444",
        aura: "aura-premium-red",
        icon: Skull,
        cta: "Subscribe Premium",
        features: ["Everything in Pro", "2 Demon Cards (Orange + Red Aura)", "High-risk parlays + boosters", "Priority drops"],
        ribbon: "Demon plays",
        savingsLine: "Under $3.30 a day \u2014 cheaper than one missed parlay",
    },
];

const FAQ = [
    { q: "How does my subscription unlock the cards?", a: "Once your payment succeeds your account is upgraded instantly. The premium cards become flippable and the slip images appear on reveal." },
    { q: "Are these picks guaranteed to win?", a: "No. Picks are for entertainment. Risk badges (low \u2192 demon) are honest signal of how aggressive each play is." },
    { q: "What happens after 7 days?", a: "Access auto-expires; you can re-subscribe anytime to keep your tier active. We don\u2019t auto-charge \u2014 you pay each cycle on your terms." },
    { q: "Can I upgrade Pro \u2192 Sports Premium?", a: "Yep. Subscribe Premium and your account immediately gets the higher tier with extended access." },
    { q: "Why is HOTSLIP so cheap compared to other lock-in services?", a: "Most paid pickers charge $40\u2013$80 a week, lock you into Discord, and disappear if they go cold. We keep it simple: $13 or $23 a week, no Discord, no upsells, cancel anytime. Every dollar goes into the picks \u2014 not into selling courses or hyped-up giveaways." },
    { q: "Need analytics, win-rate breakdowns, or stat sheets?", a: "DM our Support team directly through Messages \u2014 they handle every analytics request personally. No spreadsheets to download, no dashboards to learn. Tell them what you want (player splits, last-week recap, ROI by sport) and they\u2019ll get back to you in chat." },
];

export default function Pricing() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [loadingId, setLoadingId] = useState(null);

    const subscribe = async (id) => {
        const tokenInStorage = typeof window !== "undefined" && localStorage.getItem("token");
        if (!user && !tokenInStorage) {
            toast("Please log in to subscribe");
            nav("/login?next=/pricing");
            return;
        }
        setLoadingId(id);
        try {
            // New flow: redirect to Stripe Payment Link with user id + email pre-tagged
            const res = await api.get("/billing/payment-link", { params: { tier: id } });
            if (res.data?.url) {
                window.location.href = res.data.url;
            } else {
                throw new Error("No payment URL returned");
            }
        } catch (e) {
            if (e?.response?.status === 401) {
                toast.error("Session expired. Please log in again.");
                nav("/login?next=/pricing");
            } else if (e?.response?.status === 503) {
                toast.error("Payment system is being configured. Please try again in a few minutes.");
            } else {
                toast.error("Failed to start checkout. Please try again.");
            }
            setLoadingId(null);
        }
    };

    return (
        <div className="min-h-screen pp-bg-0 text-[#f4f4f7]">
            <Nav />
            <section className="py-16 sm:py-24">
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <div className="text-center max-w-2xl mx-auto">
                        <p className="text-xs font-mono uppercase text-[#a855f7] tracking-widest">PRICING</p>
                        <h1 className="font-display text-4xl sm:text-6xl tracking-widest text-white">PICK YOUR <span className="text-[#22c55e] neon-text-green">SIDE</span></h1>
                        <p className="mt-3 text-[#b7b7c6]">Subscribe to unlock the next level of HOTSLIP. No fluff, no Discord, just picks.</p>

                        {/* Cheapest-in-game ribbon */}
                        <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0a1f0e] border border-[#22c55e]66 shadow-[0_0_24px_rgba(34,197,94,0.18)]" data-testid="pricing-cheap-ribbon">
                            <BadgeDollarSign className="w-4 h-4 text-[#22c55e]" />
                            <span className="text-xs font-mono uppercase tracking-widest text-[#22c55e]">Half the price of every other lock-in service</span>
                        </div>
                        <p className="mt-2 text-xs text-[#8b8ba0]">Most pickers charge $40–$80 a week. We charge what it actually costs to keep the lights on.</p>
                    </div>

                    <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
                        {PLANS.map((p) => {
                            const Icon = p.icon;
                            return (
                                <Card key={p.id} className={`pp-bg-1 border-[#242436] text-white ${p.aura}`} data-testid={`plan-card-${p.id}`}>
                                    <CardContent className="p-7">
                                        <div className="flex items-center justify-between">
                                            <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest" style={{ color: p.accent }}>
                                                <Icon className="w-4 h-4" /> {p.ribbon}
                                            </span>
                                            <span className="text-xs font-mono uppercase text-[#8b8ba0]">7-day access</span>
                                        </div>
                                        <h3 className="mt-3 font-display text-3xl tracking-widest">{p.name}</h3>
                                        <div className="mt-1 flex items-baseline gap-2">
                                            <span className="font-display text-5xl tracking-widest" style={{ color: p.accent }}>{p.price}</span>
                                            <span className="text-sm text-[#8b8ba0]">{p.per}</span>
                                        </div>
                                        {p.savingsLine && (
                                            <p className="mt-1 text-[11px] font-mono uppercase tracking-widest text-[#22c55e] inline-flex items-center gap-1" data-testid={`plan-savings-${p.id}`}>
                                                <BadgeDollarSign className="w-3 h-3" /> {p.savingsLine}
                                            </p>
                                        )}
                                        <ul className="mt-5 space-y-2">
                                            {p.features.map((f, i) => (
                                                <li key={i} className="flex items-start gap-2 text-sm text-[#d6d6e0]">
                                                    <Check className="w-4 h-4 mt-0.5" style={{ color: p.accent }} />
                                                    <span>{f}</span>
                                                </li>
                                            ))}
                                        </ul>
                                        <Button onClick={() => subscribe(p.id)} disabled={loadingId === p.id} className="mt-7 w-full h-12 bg-[#6f3fec] hover:brightness-110 text-white" data-testid={`pricing-${p.id}-subscribe-button`}>
                                            {loadingId === p.id ? (
                                                <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Redirecting...</span>
                                            ) : (
                                                p.cta
                                            )}
                                        </Button>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>

                    <div className="mt-16 max-w-3xl mx-auto">
                        {/* Analytics DM Support CTA */}
                        <Card className="pp-bg-1 border-[#242436] mb-8" style={{ boxShadow: "0 0 0 1px rgba(34,211,238,0.25), 0 0 36px rgba(34,211,238,0.18)" }} data-testid="pricing-analytics-cta">
                            <CardContent className="p-6 sm:p-7 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
                                <span className="inline-flex items-center justify-center w-12 h-12 rounded-md flex-shrink-0" style={{ background: "#062229", border: "1px solid #22d3ee55" }}>
                                    <BarChart3 className="w-5 h-5 text-[#22d3ee]" />
                                </span>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-mono uppercase text-[#22d3ee] tracking-widest">Need Analytics?</p>
                                    <h3 className="mt-1 font-display text-xl sm:text-2xl tracking-wider text-white">DM SUPPORT FOR ANY ANALYTICS</h3>
                                    <p className="mt-1 text-sm text-[#b7b7c6]">Win-rate breakdowns, player splits, ROI by sport, last-week recap — DM our Support team in Messages and they'll get back to you in chat. No dashboards to learn, no spreadsheets to download.</p>
                                </div>
                                <Button asChild className="bg-[#22d3ee] hover:brightness-110 text-[#062229] font-semibold h-12 px-5 flex-shrink-0" data-testid="pricing-analytics-dm-button">
                                    <Link to={user ? "/messages" : "/login?next=/messages"} className="inline-flex items-center gap-2">
                                        <MessageSquare className="w-4 h-4" /> DM Support
                                    </Link>
                                </Button>
                            </CardContent>
                        </Card>

                        <h2 className="font-display text-2xl sm:text-3xl tracking-widest text-white">FAQ</h2>
                        <Accordion type="single" collapsible className="mt-4">
                            {FAQ.map((item, i) => (
                                <AccordionItem value={`q${i}`} key={i} className="border-[#242436]" data-testid={`faq-item-${i}`}>
                                    <AccordionTrigger className="text-white hover:text-[#a855f7] text-left">{item.q}</AccordionTrigger>
                                    <AccordionContent className="text-[#b7b7c6]">{item.a}</AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>
                </div>
            </section>
            <Footer />
        </div>
    );
}
